/**
 * Emoji cues: what the assistant's face does beyond talking. The backend sends one per event on
 * `/avatar/events` (`{"emoji":"👋","mode":"once"}`, backend harness/channels/avatar/route.py): the
 * emotion an answer starts with, and its own system moments (talk start, thinking, a search running).
 * MuseTalk and the three.js head play them as clips; the other faces react with their own means,
 * from the same cue. A replay rebuilds the cues from the recorded answers' `emoji`.
 *
 * CUE_NAMES mirrors the backend catalog (harness/channels/avatar/emojis.yaml): cues.test.ts keeps them equal.
 */
import { z } from 'zod';

export const cueModeSchema = z.enum(['once', 'loop', 'timed', 'talk', 'speak', 'express']);

export const cuePayloadSchema = z.object({
  emoji: z.string().min(1),
  mode: cueModeSchema.default('once'),
  sec: z.number().optional(),
  on: z.boolean().optional(),
}).passthrough();

export interface FaceCue {
  emoji: string;
  /** Its name in the catalog; null for an emoji the catalog does not know. */
  name: CueName | null;
  mode: z.infer<typeof cueModeSchema>;
  sec?: number;
  on?: boolean;
  /** When it arrived (ms): a new cue with the same emoji is a new reaction. */
  at: number;
}

export const CUE_NAMES = {
  '💡': 'user_detected',
  '🗣️': 'talk_start',
  '🤐': 'talk_end',
  '🤔': 'think_first',
  '😐': 'think_neutral',
  '☝️': 'think_point',
  '😊': 'happy',
  '😄': 'enthusiastic',
  '😔': 'sad',
  '😲': 'surprised',
  '🙂': 'slight_smile',
  '😌': 'reassuring',
  '😕': 'confused',
  '🎉': 'celebration',
  '👋': 'wave',
  '📱': 'tablet_on',
  '❌': 'tablet_off',
} as const;

export type CueName = (typeof CUE_NAMES)[keyof typeof CUE_NAMES];

/** Emoji are compared without the variation selector (☝ and ☝️ are the same cue). */
const bare = (emoji: string) => emoji.replace(/️/g, '');
const BY_BARE = new Map<string, CueName>(Object.entries(CUE_NAMES).map(([e, n]) => [bare(e), n]));

export function cueName(emoji: string): CueName | null {
  return BY_BARE.get(bare(emoji)) ?? null;
}

/** A cue from a `/avatar/events` frame; null if it is not one. */
export function parseCue(data: unknown, at: number): FaceCue | null {
  const r = cuePayloadSchema.safeParse(data);
  if (!r.success) return null;
  const { emoji, mode, sec, on } = r.data;
  return { emoji, name: cueName(emoji), mode, at, ...(sec !== undefined ? { sec } : {}), ...(on !== undefined ? { on } : {}) };
}
